import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { trackPageView } from "@/lib/metaPixel";
import { pushPageView } from "@/lib/gtmDataLayer";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import ScrollManager from "@/components/ScrollManager";
import OfflineGuard from "@/components/OfflineGuard";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import Orders from "@/pages/Orders";
import AuthCallback from "@/pages/AuthCallback";
import AnnouncementBanner from "@/components/AnnouncementBanner";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminPaymentIntegrity from "@/pages/admin/AdminPaymentIntegrity";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminSetup from "@/pages/AdminSetup";
// iter98 / Session E — employee-admin surface
import AdminLogin from "@/pages/AdminLogin";
import EmployeeOrders from "@/pages/EmployeeOrders";
import VerifyEmail from "@/pages/VerifyEmail";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";

import TermsConditions from "@/pages/policies/TermsConditions";
import PrivacyPolicy from "@/pages/policies/PrivacyPolicy";
import ShippingPolicy from "@/pages/policies/ShippingPolicy";
import ReturnRefundPolicy from "@/pages/policies/ReturnRefundPolicy";
import FSSAIPage from "@/pages/FSSAIPage";
import Contact from "@/pages/Contact";

import {
  LegacyRedirect,
  LegacyProductRedirect,
  ProductIdRedirect,
  NotFound,
} from "@/pages/LegacyRedirects";

function Shell() {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/super-admin");
  // iter102 / Session H — the three legacy public admin URLs must
  // render a page with ZERO visible content (no header, no footer, no
  // banner, no chrome).  This set is intentionally exhaustive so
  // visiting them reveals nothing about the new obscured admin URLs.
  const BLANK_ROUTES = new Set(["/admin", "/admin-login", "/secure-admin-login"]);
  const isBlank = BLANK_ROUTES.has(loc.pathname);
  useEffect(() => {
    trackPageView();
    pushPageView(loc.pathname + loc.search);
  }, [loc.pathname, loc.search]);

  // iter101 — customer-facing media protection.  Only registered on
  // non-admin routes so admin dashboards keep their normal right-click /
  // drag / open-in-new-tab behaviour (needed for product/image
  // management).  Neutralises the ordinary browser save/copy/drag
  // interactions on IMG / PICTURE / VIDEO / SOURCE elements only —
  // never blocks right-click on text, links, buttons or the surrounding
  // UI.  Determined users with DevTools can still fetch assets; this is
  // deliberate practical protection, not a security guarantee.
  //
  // Registered BEFORE the AuthCallback early-return so React's
  // rules-of-hooks are satisfied.
  useEffect(() => {
    if (isAdmin) return;
    const isMediaTarget = (el) => {
      if (!el || !el.closest) return false;
      return !!el.closest("img, picture, video, source");
    };
    const onContextMenu = (e) => {
      if (isMediaTarget(e.target)) e.preventDefault();
    };
    const onDragStart = (e) => {
      if (isMediaTarget(e.target)) e.preventDefault();
    };
    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("dragstart", onDragStart);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("dragstart", onDragStart);
    };
  }, [isAdmin]);

  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  // iter102 / Session H — for the three legacy admin URLs, return a
  // completely empty document body so no header, footer, banner, link
  // or text is rendered anywhere.
  if (isBlank) return null;
  const showBanner =
    !isAdmin && (loc.pathname === "/" || loc.pathname.startsWith("/shop"));

  return (
    <div
      className="min-h-screen flex flex-col"
      data-media-protected={isAdmin ? undefined : "1"}
    >
      <ScrollManager />
      <Header />
      {showBanner && <AnnouncementBanner />}
      <main className="flex-1">
        <Routes>
          {/* ── canonical app routes ────────────────────────────── */}
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          {/* iter63 — SEO slug PDPs: /pickles/avakaya-pickle etc. */}
          <Route path="/product/:id" element={<ProductIdRedirect />} />
          {/* Category landing pages (rendered by Shop with a filter). */}
          <Route path="/pickles" element={<Shop categorySlug="pickles" />} />
          <Route path="/pastes" element={<Shop categorySlug="pastes" />} />
          <Route path="/wood-pressed-oils" element={<Shop categorySlug="wood-pressed-oils" />} />
          <Route path="/combos" element={<Shop categorySlug="combos" />} />
          <Route path="/podis" element={<Shop categorySlug="podis" />} />
          <Route path="/masala-powders" element={<Shop categorySlug="masala-powders" />} />
          <Route path="/spices" element={<Shop categorySlug="spices" />} />
          <Route path="/flours" element={<Shop categorySlug="flours" />} />
          {/* The slug-routed PDP — last among the two-segment routes so
              the static category routes above take precedence. */}
          <Route path="/:category/:slug" element={<ProductDetail />} />

          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:id" element={<OrderConfirmation />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/verify-email" element={<VerifyEmail />} />
          <Route path="/auth/confirm-email-change" element={<ConfirmEmailChange />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          {/* iter102 / Session H — obscured admin login URLs. */}
          {/* Employee Admin login moved from /admin-login to a hard-to-guess
              path.  The Employee Admin account, its authentication, its
              permissions and its dashboards are ALL unchanged — only this
              route path is different. */}
          <Route path="/admin-sdf-orders-ops-2482-login" element={<AdminLogin />} />
          {/* Super Admin login now has its own dedicated public URL.
              Renders the same customer `<Login />` component (which
              already handles Super Admin email + password + TOTP 2FA
              challenges via /api/auth/login) so no backend auth path or
              security control is changed.  Only the URL is obscured. */}
          <Route path="/secure-admin-sdf-dashboard-starcommand-e489t-login" element={<Login />} />
          <Route path="/admin-orders" element={<EmployeeOrders />} />
          <Route path="/account" element={<Account />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderConfirmation />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms-and-conditions" element={<TermsConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/return-refund-policy" element={<ReturnRefundPolicy />} />
          <Route path="/fssai" element={<FSSAIPage />} />

          {/* ── iter63 legacy Pocketlite redirects ───────────────── */}
          {/* /home/* — old PHP-style routes.  Case-insensitive routing is
               achieved by registering both /home/* and /Home/*. */}
          <Route path="/home" element={<LegacyRedirect to="/" />} />
          <Route path="/Home" element={<LegacyRedirect to="/" />} />
          <Route path="/home/contact" element={<LegacyRedirect to="/contact" />} />
          <Route path="/Home/contact" element={<LegacyRedirect to="/contact" />} />
          <Route path="/home/login" element={<LegacyRedirect to="/login" />} />
          <Route path="/Home/login" element={<LegacyRedirect to="/login" />} />
          <Route path="/home/brands" element={<LegacyRedirect to="/shop" />} />
          <Route path="/Home/brands" element={<LegacyRedirect to="/shop" />} />
          <Route path="/home/return_policy" element={<LegacyRedirect to="/return-refund-policy" />} />
          <Route path="/Home/return_policy" element={<LegacyRedirect to="/return-refund-policy" />} />
          <Route path="/home/privacy_policy" element={<LegacyRedirect to="/privacy-policy" />} />
          <Route path="/Home/privacy_policy" element={<LegacyRedirect to="/privacy-policy" />} />
          <Route path="/home/terms" element={<LegacyRedirect to="/terms-and-conditions" />} />
          <Route path="/Home/terms" element={<LegacyRedirect to="/terms-and-conditions" />} />
          <Route path="/home/shipping_policy" element={<LegacyRedirect to="/shipping-policy" />} />
          <Route path="/Home/shipping_policy" element={<LegacyRedirect to="/shipping-policy" />} />
          <Route path="/home/about" element={<LegacyRedirect to="/" />} />
          <Route path="/Home/about" element={<LegacyRedirect to="/" />} />
          <Route path="/home/product_details/:id" element={<LegacyProductRedirect />} />
          <Route path="/Home/product_details/:id" element={<LegacyProductRedirect />} />
          <Route path="/products" element={<LegacyRedirect to="/shop" />} />
          <Route path="/Products" element={<LegacyRedirect to="/shop" />} />
          <Route path="/category/:cat" element={<LegacyRedirect to="/shop" />} />

          {/* iter99 — Super Admin URLs migrated to /super-admin/*
              (browser-facing only; backend API routes /api/admin/*
              remain unchanged).  Legacy /admin/* URLs auto-redirect
              via <LegacyAdminRedirect> below so bookmarks / links
              keep working — the redirect goes through AdminLayout so
              role authorization is still enforced. */}
          <Route path="/super-admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="homepage" element={<AdminHomepage />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="payment-integrity" element={<AdminPaymentIntegrity />} />
          </Route>
          {/* iter102 / Session H — the three legacy public admin URLs
              must render a COMPLETELY BLANK page: no login form, no
              text, no redirect (so the new obscured URLs are never
              revealed by simply visiting the old ones).  These specific
              routes take precedence over the /:categorySlug catch-all
              below AND the /admin/* catch-all further down. */}
          <Route path="/admin-login" element={<BlankPage />} />
          <Route path="/secure-admin-login" element={<BlankPage />} />
          <Route path="/admin" element={<BlankPage />} />
          {/* The /admin/* legacy redirect is intentionally removed so
              typing /admin/anything no longer leaks the /super-admin
              tree via a URL rewrite.  Legitimate super-admin URLs
              already live at /super-admin/* — see routes above. */}

          {/* ── iter79 dynamic single-segment category catch-all ─────
               Matches any admin-created category slug like `/whole-spices`.
               MUST come last (right before the 404) so every static
               single-segment route above still takes priority. */}
          <Route path="/:categorySlug" element={<Shop />} />

          {/* ── 404 catch-all ───────────────────────────────────── */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppFab />}
    </div>
  );
}

/**
 * iter102 / Session H — BlankPage renders literally nothing.
 *
 * Used for the three legacy admin URLs (/admin, /admin-login,
 * /secure-admin-login) so visiting them reveals absolutely no
 * content — no login form, no error message, no redirect that would
 * expose the new obscured URLs.  The header/footer chrome around it
 * still renders because those are part of the Shell (an admin surface
 * matcher hides them for /super-admin/* only).  If you need those
 * fully hidden too, adjust ``isAdmin`` in Shell.
 */
function BlankPage() {
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SettingsProvider>
          <CartProvider>
            <BrowserRouter>
              <Toaster richColors position="top-center" closeButton />
              <OfflineGuard />
              <Shell />
            </BrowserRouter>
          </CartProvider>
        </SettingsProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}
